/**
 * Public verify page for TRAINING CERTIFICATES — /verify/certificate/:certId
 * Publicly verifiable (no auth) — auditors can validate SHA-256 hash and issuing metadata.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { BadgeCheck, AlertTriangle, Loader2, ArrowRight, GraduationCap, Hash, Copy, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import useMetaTags from '../../hooks/useMetaTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FW_UI = {
  nis2: { tone: 'from-sky-500 to-sky-700', label: 'NIS2 · Art. 21(2)(g)' },
  ai_act: { tone: 'from-violet-500 to-violet-700', label: 'EU AI Act · Art. 4' },
  both: { tone: 'from-indigo-500 to-indigo-700', label: 'NIS2 + EU AI Act' },
};

export default function VerifyCertificate() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useMetaTags({
    title: state.data?.valid ? `Certificado ${certId} · NIS2AI` : 'Verificação de Certificado · NIS2AI',
    description: 'Verificação pública de certificado de formação NIS2AI com assinatura SHA-256.',
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/verify/certificate/${certId}`);
        setState({ loading: false, data, error: null });
      } catch (err) {
        setState({ loading: false, data: null, error: err?.response?.data?.detail || 'Falha ao verificar.' });
      }
    })();
  }, [certId]);

  const copyHash = () => {
    if (!state.data?.certificate_hash) return;
    navigator.clipboard.writeText(state.data.certificate_hash);
    toast.success('Hash copiado.');
  };

  const invalid = !state.loading && (!state.data?.valid || state.error);
  const fw = state.data?.framework;
  const fwMeta = FW_UI[fw] || FW_UI.both;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="verify-cert-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-sm mb-8">
          <ShieldCheck className="h-4 w-4" /> NIS2AI · Verificação Pública
        </Link>

        {state.loading && (
          <div className="p-16 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-600" />
            <p className="text-sm text-slate-500 mt-3">A verificar certificado…</p>
          </div>
        )}

        {invalid && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center" data-testid="verify-invalid">
            <AlertTriangle className="h-12 w-12 mx-auto text-red-600 mb-3" />
            <h1 className="text-xl font-semibold text-red-900 mb-2">Certificado inválido</h1>
            <p className="text-sm text-red-700">
              {state.data?.reason || state.error || 'Este certificado não pôde ser verificado.'}
            </p>
            <div className="text-xs text-red-500 mt-4 font-mono">ID: {certId}</div>
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-red-700 underline mt-6 hover:text-red-900">
              Voltar à página inicial <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        )}

        {state.data?.valid && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="verify-valid">
            <div className={`bg-gradient-to-r ${fwMeta.tone} text-white p-8 text-center`}>
              <BadgeCheck className="h-16 w-16 mx-auto mb-3" />
              <div className="text-xs uppercase tracking-widest opacity-80 mb-1">Certificado válido · Assinado SHA-256</div>
              <h1 className="text-3xl font-bold">{state.data.recipient_name}</h1>
              <div className="text-sm opacity-90 mt-1">{state.data.recipient_email_masked}</div>
            </div>

            <div className="p-8 space-y-6">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Concluiu com aproveitamento</div>
                <h2 className="text-xl font-semibold text-slate-900 mt-1 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-indigo-600" />
                  {state.data.course_title}
                </h2>
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  {state.data.framework_label}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-center" data-testid="verify-score">
                  <div className="text-3xl font-bold text-emerald-700 tabular-nums">{state.data.score}%</div>
                  <div className="text-xs text-emerald-600 mt-1">Nota obtida (mínimo {state.data.pass_threshold}%)</div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-center">
                  <div className="text-lg font-semibold text-slate-800">{(state.data.issued_at || '').slice(0, 10) || '—'}</div>
                  <div className="text-xs text-slate-500 mt-1">Data de emissão</div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1"><Hash className="h-3 w-3" /> Assinatura criptográfica</span>
                  <button
                    onClick={copyHash}
                    className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-[11px]"
                    data-testid="verify-copy-hash"
                  >
                    <Copy className="h-3 w-3" /> Copiar
                  </button>
                </div>
                <code className="text-[11px] text-slate-700 font-mono break-all block" data-testid="verify-cert-hash">
                  {state.data.certificate_hash}
                </code>
                <div className="text-[10px] text-slate-400 mt-2">
                  Este hash é gerado deterministicamente a partir de <code>curso · destinatário · nota · data · id</code> e pode ser recomputado a partir dos dados armazenados na base de dados de origem.
                </div>
              </div>

              <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-4 flex items-center justify-between">
                <span>Verificado em <time>{(state.data.verified_at || '').slice(0, 19).replace('T', ' ')}</time></span>
                <span className="font-mono">{state.data.cert_id}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
